import { z } from "zod";
import type {
  BuildPipelineContext,
  ImageProvider,
  PipelineNode,
  PipelineSpec,
  Ref,
  ToolCore,
  ToolModelDef,
} from "@loumy/contracts";
import { modelEnum, resolveModelProvider, selectModel, withModelGating } from "@loumy/contracts";
import { CAP } from "@loumy/capabilities";

// §10.2 Image-Generator. DAG (§6):
//   prompt-enhance → image-gen → chroma-key[fanOut über images, condition needsChromaKey,
//   passThrough image←image]. outputs.images ← key.image (Collection). Pass-Through liefert
//   rohe Bilder typ-rein bei needsChromaKey=false (D30).

const ASPECT_RATIOS = ["1:1", "3:2", "2:3", "16:9", "9:16"] as const;
type AspectRatio = (typeof ASPECT_RATIOS)[number];

// D101: die Modell-Liste ist über einem expliziten TEIL-Feldtyp deklariert (nur die Felder,
// die Modelle anfassen) — bricht die Typ-Zirkularität models ↔ z.infer(Schema) und hält die
// defaults/hiddenSettings trotzdem key-typisiert.
type ImageGeneratorModelFields = { aspectRatio: AspectRatio };

/**
 * D101: wählbare Modelle — reine Deklarationsdaten (§13-austauschbar), `models[0]` = Default.
 * v1 = Mechanismus mit dem bestehenden Qwen-Provider (D63): 'Loco Portrait' fixiert das Seiten-
 * verhältnis (Default 2:3 + Feld versteckt) — beweist defaults+hiddenSettings end-to-end.
 * NAMEN (User-Entscheid): markeneigene „Loco"-Familie statt Original-Engine-Namen — die Engine
 * ist eine austauschbare Bindung (providerBindings/Slot-Default), kein Produktname; `Loco Turbo`
 * ist fürs erste echte Zweitmodell reserviert. Echte Zweit-AI-Modelle (eigener fal.ai-Adapter +
 * providerBindings + '#'-Preis) = Folge-ADRs.
 */
export const IMAGE_GENERATOR_MODELS: readonly ToolModelDef<ImageGeneratorModelFields>[] = [
  { id: "loco-core", label: "Loco Core", description: "Standard-Bildmodell — alle Einstellungen frei." },
  {
    id: "loco-portrait",
    label: "Loco Portrait",
    description: "Hochformat für Charaktere & Porträts — Seitenverhältnis fest auf 2:3.",
    defaults: { aspectRatio: "2:3" },
    hiddenSettings: ["aspectRatio"],
  },
];

export const ImageGeneratorSettings = withModelGating(
  z.object({
    model: modelEnum(IMAGE_GENERATOR_MODELS),
    prompt: z.string().min(1).max(2000),
    aspectRatio: z.enum(ASPECT_RATIOS).default("1:1"),
    count: z.number().int().min(1).max(4).default(1),
  }),
  IMAGE_GENERATOR_MODELS,
);
export type ImageGeneratorSettings = z.infer<typeof ImageGeneratorSettings>;

/**
 * Megapixel aus „WxH" (eingefroren für die Pricing-Metrik, §10.2/§3.6).
 * FAIL-CLOSED (D44): eine nicht parsebare/ungültige Auflösung WIRFT — kein stiller
 * 1-MP-Fallback (der einen Hold unterdecken würde). buildPipeline scheitert dann,
 * bevor ein Job/Hold entsteht.
 */
export function megapixelsOf(resolution: string): number {
  const m = /^(\d+)x(\d+)$/i.exec(resolution.trim());
  if (!m)
    throw new Error(`Ungültige Auflösung '${resolution}' (erwarte 'BxH') — Megapixel-Metrik unbestimmbar (fail-closed, D44).`);
  const mp = (Number(m[1]) * Number(m[2])) / 1_000_000;
  if (!Number.isFinite(mp) || mp <= 0)
    throw new Error(`Auflösung '${resolution}' ergibt keine gültige Megapixel-Metrik (fail-closed, D44).`);
  return mp;
}

export const imageGeneratorCore: ToolCore<ImageGeneratorSettings> = {
  id: "image-generator",
  category: "image",
  inputs: [{ kind: "image", min: 0, max: 2, role: "reference" }],
  settingsSchema: ImageGeneratorSettings,
  models: IMAGE_GENERATOR_MODELS,
  outputSlots: [{ name: "images", kind: "image", optional: false, collection: true }],
  buildPipeline(settings, inputs, ctx: BuildPipelineContext): PipelineSpec {
    // Single-Pass (D34): Auflösung deterministisch (D30), Gesamt-Megapixel eingefroren.
    // D101: der image-Provider kommt über die MODELL-Bindung (heute überall der Slot-Default;
    // ein Modell mit providerBindings zieht hier automatisch seinen Adapter — und über den
    // gepinnten providerId-Param seinen '#'-Preis-Key).
    const model = selectModel(IMAGE_GENERATOR_MODELS, settings);
    const image = resolveModelProvider<ImageProvider>(ctx.providers, model, "image");
    const { resolution } = image.resolve(settings.aspectRatio);
    const megapixel = megapixelsOf(resolution) * settings.count; // count × MP/Bild (§10.2)

    // 0–2 Referenz-Bilder → $input-Ports (Kind-Check via '$input' übersprungen). Mit Referenzen
    // routet der Provider (Qwen 2.0) auf den EDIT-Endpoint (D76, maxRefs 3) — Bild+Prompt =
    // editiere das Bild. Fail-closed BEIM BUILD gegen die Fähigkeit des gepinnten Adapters (D34):
    // mehr Refs als unterstützt → klar hier abbrechen (nicht erst nach dem Credit-Hold).
    if (inputs.length > image.limits.maxRefs)
      throw new Error(
        `image-generator: ${inputs.length} Referenzbild(er) übergeben, aber Provider '${image.id}' unterstützt nur ${image.limits.maxRefs} (D34).`,
      );
    const refInputs: Record<string, Ref> = {};
    if (inputs[0]) refInputs.reference1 = { node: "$input", port: "reference", ordinal: 0 };
    if (inputs[1]) refInputs.reference2 = { node: "$input", port: "reference", ordinal: 1 };
    const genInputs = refInputs;

    const nodes: PipelineNode[] = [
      {
        id: "enhance",
        capabilityId: CAP.promptEnhance.id,
        capabilityVersion: CAP.promptEnhance.version,
        surface: "server",
        // D80: dieselben Referenzbilder wie gen → der LLM SIEHT das hochgeladene Bild (Edit-Modus,
        // §10.2) und bewahrt dessen Stil, statt blind „hyperrealistic" zu ergänzen.
        inputs: { ...refInputs },
        params: { prompt: { from: "settings", key: "prompt" } },
      },
      {
        id: "gen",
        capabilityId: CAP.imageGen.id,
        capabilityVersion: CAP.imageGen.version,
        surface: "server",
        inputs: genInputs,
        params: {
          aspectRatio: settings.aspectRatio,
          count: settings.count,
          megapixel,
          providerId: image.id,
          providerVersion: image.version,
          resolution,
          // veredelter Prompt aus enhance (ScalarRef → Topo-Kante enhance→gen, D41).
          prompt: { from: "node", node: "enhance", port: "prompt" },
        },
      },
      {
        id: "key",
        capabilityId: CAP.chromaKey.id,
        capabilityVersion: CAP.chromaKey.version,
        surface: "server",
        inputs: { image: { node: "gen", port: "images" } }, // per-Element via fanOut
        fanOut: { over: { node: "gen", port: "images", collection: true } },
        condition: { fromNode: "enhance", signal: "needsChromaKey", op: "truthy", onMissing: "skip" },
        passThrough: { image: "image" }, // needsChromaKey=false → rohe Bilder typ-rein (D30)
        params: {
          count: settings.count, // Fan-out-Menge eingefroren (D42) für Pricing
          // D97: der chroma-key entfernt EXAKT die vom Enhancer gewählte Screen-Farbe (Kontrast
          // zum Subjekt: viel Grün → blue, viel Blau → green) statt "auto"-Sampling — dieselbe
          // ScalarRef-Mechanik wie validate.keyColor im Spritesheet (D79, deterministisch D34).
          keyColor: { from: "node", node: "enhance", port: "keyColor" },
        },
      },
    ];

    return { nodes, outputs: { images: { ref: { node: "key", port: "image", collection: true } } } };
  },
};
